<template>
  <div class="mobile-home-view">
    <!-- Mobile Header -->
    <MobileHeader
      :cart-count="cartCount"
      :products="products"
      @cart="goToCart"
      @search="handleSearch"
      @product-select="viewProduct"
    />

    <!-- Main Content -->
    <main class="mobile-content" :style="{ paddingBottom: '80px' }">
      <!-- Collection Banner (Reversed) -->
      <section class="mobile-section">
        <div class="collection-banner">
          <div class="collection-image">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/bio-edge.firebasestorage.app/o/ChatGPT%20Image%20Oct%2023%2C%202025%2C%2009_02_25%20PM.png?alt=media&token=14e92603-4f6f-45fe-bf05-09f51c7a8ddd"
              alt="BioEdge Collection"
              class="collection-img"
            />
          </div>
          <div class="collection-content">
            <h2 class="collection-title">Premium Collection</h2>
            <p class="collection-subtitle">Discover our complete range of wellness solutions</p>
            <button class="collection-btn" @click="$router.push('/products')">
              Explore Collection
            </button>
          </div>
        </div>
      </section>

      <!-- Category Section -->
      <section class="mobile-section">
        <div class="section-header">
          <h2 class="section-title">Category</h2>
          <button class="see-all-btn" @click="$router.push('/products')">See All</button>
        </div>
        
        <div class="categories-grid">
          <div
            v-for="category in categories"
            :key="category.id || category"
            class="category-item"
            @click="goToCategory(category)"
          >
            <div class="category-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <span class="category-label">{{ category.name || category }}</span>
          </div>
        </div>
      </section>

      <!-- Latest Added Section -->
      <section class="mobile-section">
        <div class="section-header">
          <h2 class="section-title">Latest Added</h2>
          <button class="see-all-btn" @click="$router.push('/products')">See All</button>
        </div>
        
        <div class="products-scroll">
          <div
            v-for="(product, index) in filteredProducts"
            :key="product.firebaseId || product.id || index"
            class="mobile-product-card"
            @click="viewProduct(product)"
            style="cursor: pointer;"
          >
            <div class="product-card-image">
              <img
                :src="productCardImageUrl(product)"
                :alt="product.name"
                @error="handleImageError"
              />
            </div>
            <div class="product-card-info">
              <h4 class="product-name">{{ product.name }}</h4>
              <p class="product-category">{{ product.category || 'BioEdge' }}</p>
              <div class="product-price-row">
                <span class="product-price">R{{ formatPrice(product.price) }}</span>
                <button
                  class="add-cart-btn"
                  @click.stop="addToCart(product)"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Mobile Cart Drawer -->
    <MobileCartDrawer
      :visible="cartVisible"
      :items="cartItems"
      :total="cartTotal"
      :format-price="formatPrice"
      :get-item-price="getCartItemPrice"
      @close="toggleCart"
      @increase="increaseCartItem"
      @decrease="decreaseCartItem"
      @remove="removeFromCart"
      @checkout="checkoutCart"
    />

    <!-- Bottom Navigation -->
    <MobileBottomNav />
  </div>
</template>

<script>
import MobileHeader from '../components/MobileHeader.vue'
import MobileBottomNav from '../components/MobileBottomNav.vue'
import MobileCartDrawer from '../components/MobileCartDrawer.vue'
import { productService, categoryService } from '../services/firebaseService'
import { getProductImageUrl } from '../utils/productImageUrl'

export default {
  name: 'MobileHomeView',
  components: {
    MobileHeader,
    MobileBottomNav,
    MobileCartDrawer
  },
  data() {
    return {
      products: [],
      categories: [],
      searchQuery: '',
      cartVisible: false,
      cartItems: [],
      favorites: []
    }
  },
  computed: {
    cartCount() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0)
    },
    cartTotal() {
      return this.cartItems.reduce(
        (total, item) => total + this.getCartItemPrice(item) * (item.quantity || 0),
        0
      )
    },
    filteredProducts() {
      // Show latest added products (sorted by timestamp, newest first)
      return [...this.products]
        .sort((a, b) => this.getProductTimestamp(b) - this.getProductTimestamp(a))
        .slice(0, 10) // Show top 10 latest
    }
  },
  methods: {
    productCardImageUrl(product) {
      return (
        getProductImageUrl(product) ||
        'https://via.placeholder.com/200/f3f4f6/52CD36?text=BioEdge'
      )
    },
    async loadProducts() {
      try {
        const firebaseProducts = await productService.getAllProducts()
        this.products = firebaseProducts.map(product => ({
          ...product,
          price: typeof product.price === 'number' ? product.price :
                 typeof product.price === 'string' ? parseFloat(product.price.replace(/[^\d.]/g, '')) || 0 : 0,
          firebaseId: product.id
        }))
      } catch (error) {
        console.error('Error loading products:', error)
        this.products = []
      }
    },
    async loadCategories() {
      try {
        const categories = await categoryService.getAllCategories()
        this.categories = categories.slice(0, 4) // Show top 4 categories
      } catch (error) {
        console.error('Error loading categories:', error)
        this.categories = []
      }
    },
    getProductTimestamp(product) {
      const source = product?.createdAt || product?.updatedAt || product?.timestamp || product?.created_at
      if (source && typeof source === 'object' && typeof source.seconds === 'number') {
        return source.seconds * 1000 + (source.nanoseconds || 0) / 1e6
      }
      if (typeof source === 'string') {
        const parsed = Date.parse(source)
        return Number.isNaN(parsed) ? 0 : parsed
      }
      if (typeof source === 'number') return source
      return 0
    },
    handleSearch(query) {
      this.searchQuery = query
    },
    goToCart() {
      this.toggleCart()
    },
    viewProduct(product) {
      const productId = product.firebaseId || product.id
      const productName = product.name
      
      if (productId) {
        this.$router.push({ path: `/product/${productId}`, query: { name: productName } })
      } else if (productName) {
        this.$router.push({ path: '/product', query: { name: productName } })
      }
    },
    goToCategory(category) {
      const categoryName = category.name || category
      this.$router.push({ path: '/products', query: { category: categoryName } })
    },
    toggleCart() {
      this.cartVisible = !this.cartVisible
    },
    addToCart(product) {
      const productName = typeof product === 'string' ? product : product?.name
      if (!productName) return
      
      const productObj = this.products.find(p => p.name === productName)
      if (!productObj) return
      
      const existingItem = this.cartItems.find(item => item.product === productName)
      if (existingItem) {
        existingItem.quantity += 1
      } else {
        this.cartItems.push({ product: productName, quantity: 1 })
      }
      this.saveCartToStorage()
      this.cartVisible = true
    },
    increaseCartItem(index) {
      if (this.cartItems[index]) {
        this.cartItems[index].quantity += 1
        this.saveCartToStorage()
      }
    },
    decreaseCartItem(index) {
      const item = this.cartItems[index]
      if (!item) return
      if (item.quantity > 1) {
        item.quantity -= 1
      } else {
        this.cartItems.splice(index, 1)
      }
      this.saveCartToStorage()
    },
    removeFromCart(index) {
      this.cartItems.splice(index, 1)
      this.saveCartToStorage()
    },
    checkoutCart() {
      // Navigate to products page for checkout
      this.cartVisible = false
      this.$router.push('/products')
    },
    getCartItemPrice(item) {
      const product = this.products.find(p => p.name === item.product)
      return this.getProductPrice(product)
    },
    getProductPrice(product) {
      if (!product) return 0
      if (typeof product.price === 'number') return product.price
      if (typeof product.price === 'string') {
        return parseFloat(product.price.replace(/[^\d.]/g, '')) || 0
      }
      return 0
    },
    formatPrice(price) {
      if (typeof price === 'number') {
        return price.toLocaleString('en-ZA')
      }
      if (typeof price === 'string') {
        const numericPrice = parseFloat(price.replace(/[^\d.]/g, '')) || 0
        return numericPrice.toLocaleString('en-ZA')
      }
      return '0'
    },
    toggleFavorite(product) {
      const productId = product.firebaseId || product.id
      const index = this.favorites.indexOf(productId)
      if (index > -1) {
        this.favorites.splice(index, 1)
      } else {
        this.favorites.push(productId)
      }
      this.saveFavoritesToStorage()
    },
    isFavorite(product) {
      const productId = product.firebaseId || product.id
      return this.favorites.includes(productId)
    },
    handleImageError(event) {
      event.target.src = 'https://via.placeholder.com/200'
    },
    loadCartFromStorage() {
      if (typeof window === 'undefined') return
      try {
        const stored = localStorage.getItem('bioedgeCart')
        if (!stored) return
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          this.cartItems = parsed
            .filter(item => item && item.product)
            .map(item => ({
              product: item.product,
              quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1
            }))
        }
      } catch (error) {
        console.warn('Unable to load cart:', error)
      }
    },
    saveCartToStorage() {
      if (typeof window === 'undefined') return
      try {
        localStorage.setItem('bioedgeCart', JSON.stringify(this.cartItems))
      } catch (error) {
        console.warn('Unable to save cart:', error)
      }
    },
    loadFavoritesFromStorage() {
      if (typeof window === 'undefined') return
      try {
        const stored = localStorage.getItem('bioedgeFavorites')
        if (stored) {
          this.favorites = JSON.parse(stored)
        }
      } catch (error) {
        console.warn('Unable to load favorites:', error)
      }
    },
    saveFavoritesToStorage() {
      if (typeof window === 'undefined') return
      try {
        localStorage.setItem('bioedgeFavorites', JSON.stringify(this.favorites))
      } catch (error) {
        console.warn('Unable to save favorites:', error)
      }
    }
  },
  async mounted() {
    this.loadCartFromStorage()
    this.loadFavoritesFromStorage()
    await Promise.all([this.loadProducts(), this.loadCategories()])
  }
}
</script>

<style scoped>
.mobile-home-view {
  min-height: 100vh;
  background: #f9fafb;
  font-family: 'Montserrat', sans-serif;
}

.mobile-content {
  padding: 0;
}

.mobile-section {
  padding: 1.5rem 1rem;
  background: white;
  margin-bottom: 0.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
}

.see-all-btn {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #52CD36;
  background: none;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Collection Banner (Reversed) */
.collection-banner {
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  border-radius: 1rem;
  overflow: hidden;
  position: relative;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
}

.collection-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  order: 1;
}

.collection-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.collection-content {
  padding: 2rem 1.5rem;
  color: white;
  text-align: center;
  order: 2;
}

.collection-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.75rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  color: white;
}

.collection-subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  opacity: 0.95;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.collection-btn {
  font-family: 'Montserrat', sans-serif;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.collection-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Categories */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 1rem;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.category-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(82, 205, 54, 0.3);
}

.category-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(82, 205, 54, 0.4);
}

.category-icon svg {
  width: 2rem;
  height: 2rem;
  fill: white;
  transition: fill 0.2s;
}

.category-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-align: center;
}

/* Products */
.products-scroll {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.products-scroll::-webkit-scrollbar {
  display: none;
}

.mobile-product-card {
  flex: 0 0 160px;
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-card-image {
  position: relative;
  width: 100%;
  height: 160px;
  background: #f3f4f6;
}

.product-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.favorite-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.favorite-btn svg {
  width: 1.125rem;
  height: 1.125rem;
  fill: #ef4444;
}

.favorite-btn:hover {
  background: white;
  transform: scale(1.1);
}

.product-card-info {
  padding: 0.75rem;
}

.product-name {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.product-category {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.product-price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-price {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #52CD36;
}

.add-cart-btn {
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.add-cart-btn svg {
  width: 1.125rem;
  height: 1.125rem;
  fill: white;
}

.add-cart-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(82, 205, 54, 0.4);
}

.cart-slide-enter-active,
.cart-slide-leave-active {
  transition: transform 0.3s ease;
}

.cart-slide-enter-from,
.cart-slide-leave-to {
  transform: translateX(100%);
}

@media (min-width: 1024px) {
  .mobile-home-view {
    display: none;
  }
}
</style>

